import React, { useEffect, useMemo, useRef, useState } from 'react';
import { parseFlow } from '../utils/flowParser';
import { parseStepFlow, toStepParsedFlow } from '../utils/stepFlow';
import type { DocImportResult } from '../hooks/sessionTypes';
import '../styles/ImportFromDocumentModal.css';

export type FlowSourceGrammar = 'steps' | 'domain';
export interface FlowSourceSaveArgs {
  flowText: string;
  force?: boolean;
  expectedRevision?: string;
}
export interface FlowSourceSaveResult extends DocImportResult {
  revision?: string;
}
export interface FlowSourceBinding {
  grammar: FlowSourceGrammar;
  label: string;
  currentFlowText: string;
  onSave: (args: FlowSourceSaveArgs) => Promise<FlowSourceSaveResult>;
  revision?: string;
  strict?: boolean;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string | null;
  currentFlowText: string;
  onSave: (args: FlowSourceSaveArgs) => Promise<FlowSourceSaveResult>;
  isSaving?: boolean;
  grammar?: FlowSourceGrammar;
  strict?: boolean;
  sources?: FlowSourceBinding[];
  sourceScopeKey?: string;
}

const countSteps = (text: string) => {
  const walk = (steps: ReturnType<typeof parseStepFlow>['steps']): number =>
    steps.reduce((count, step) => count + 1 + walk(step.steps), 0);
  return walk(parseStepFlow(text).steps);
};

function mismatchMessage(text: string, grammar: FlowSourceGrammar): string | null {
  if (!text.trim()) return null;
  const domain = parseFlow(text);
  const step = parseStepFlow(text);
  const validDomain = domain.statements.length > 0 && !domain.errors.some((issue) => issue.severity === 'error');
  const validSteps = countSteps(text) > 0 && step.errors.length === 0;
  if (grammar === 'steps' && validDomain) {
    return 'This looks like Domain interactions source. Select Domain interactions before saving it.';
  }
  if (grammar === 'domain' && validSteps && domain.errors.some((issue) => issue.severity === 'error')) {
    return 'This looks like Process steps source. Select Process steps before saving it.';
  }
  return null;
}

const FlowSourceEditModal: React.FC<Props> = ({
  isOpen, onClose, sessionId, currentFlowText, onSave, isSaving = false,
  grammar = 'domain', strict = false, sources, sourceScopeKey,
}) => {
  const fallback: FlowSourceBinding = {
    grammar, label: grammar === 'steps' ? 'Process steps' : 'Domain interactions',
    currentFlowText, onSave, strict,
  };
  const bindings = sources?.length ? sources : [fallback];
  const resetKey = sourceScopeKey ?? currentFlowText;
  const [activeGrammar, setActiveGrammar] = useState<FlowSourceGrammar>(bindings[0].grammar);
  const [drafts, setDrafts] = useState<Partial<Record<FlowSourceGrammar, string>>>({});
  const [dirty, setDirty] = useState<Partial<Record<FlowSourceGrammar, boolean>>>({});
  const [openedRevisions, setOpenedRevisions] = useState<Partial<Record<FlowSourceGrammar, string | undefined>>>({});
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<{ existingStatementCount: number } | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const scopeGeneration = useRef(0);

  useEffect(() => {
    if (!isOpen) return;
    scopeGeneration.current += 1;
    const initial = bindings.reduce<Partial<Record<FlowSourceGrammar, string>>>((next, binding) => {
      next[binding.grammar] = binding.currentFlowText || '';
      return next;
    }, {});
    setActiveGrammar(bindings[0].grammar);
    setDrafts(initial);
    setOpenedRevisions(bindings.reduce<Partial<Record<FlowSourceGrammar, string | undefined>>>((next, binding) => {
      next[binding.grammar] = binding.revision;
      return next;
    }, {}));
    setDirty({});
    setError(null);
    setConflict(null);
    const timer = setTimeout(() => textareaRef.current?.focus(), 0);
    return () => clearTimeout(timer);
    // A source scope identifies the document opened for editing. Incoming refreshes
    // within that scope must not replace either unsaved draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, resetKey]);

  const active = bindings.find((binding) => binding.grammar === activeGrammar) ?? bindings[0];
  const draft = drafts[active.grammar] ?? active.currentFlowText ?? '';
  const parseResult = useMemo(() => {
    if (!draft.trim()) return active.grammar === 'steps' ? toStepParsedFlow('') : parseFlow('');
    try {
      return active.grammar === 'steps' ? toStepParsedFlow(draft) : parseFlow(draft);
    } catch (caught) {
      return {
        statements: [], actors: [], systems: [], targets: [],
        errors: [{ statementId: null, severity: 'error' as const, code: 'parse_threw', message: (caught as Error).message, source: null }],
      };
    }
  }, [active.grammar, draft]);
  const errCount = parseResult.errors.filter((issue) => issue.severity === 'error').length;
  const warnCount = parseResult.errors.filter((issue) => issue.severity === 'warning').length;

  const handleSave = async (force = false) => {
    if (!sessionId) { setError('No active session — start a session before editing.'); return; }
    const mismatch = active.strict ? mismatchMessage(draft, active.grammar) : null;
    if (mismatch) { setError(mismatch); return; }
    setError(null);
    setConflict(null);
    const args: FlowSourceSaveArgs = { flowText: draft, force };
    const openedRevision = openedRevisions[active.grammar];
    if (openedRevision !== undefined) args.expectedRevision = openedRevision;
    const savingGeneration = scopeGeneration.current;
    const result = await active.onSave(args);
    if (scopeGeneration.current !== savingGeneration) return;
    if (!result.ok) {
      if (result.status === 409 && result.conflict) setConflict(result.conflict);
      else setError(result.error || 'Save failed.');
      return;
    }
    if (result.revision !== undefined) {
      setOpenedRevisions((previous) => Object.fromEntries(
        Object.keys(previous).map((key) => [key, result.revision]),
      ));
    }
    const remainingDirty = Object.entries(dirty).some(([key, value]) => key !== active.grammar && value);
    setDirty((previous) => ({ ...previous, [active.grammar]: false }));
    if (!remainingDirty) onClose();
  };

  if (!isOpen) return null;
  const multipleSources = bindings.length > 1;
  const textareaLabel = multipleSources ? `${active.label} source` : 'Flow grammar';
  return (
    <div className="ifd-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget && !isSaving) onClose(); }}>
      <div className="ifd-modal" role="dialog" aria-modal="true" aria-labelledby="fse-title">
        <header className="ifd-head">
          <h2 id="fse-title">Edit flow source</h2>
          <button className="ifd-close" onClick={onClose} disabled={isSaving} aria-label="Close">×</button>
        </header>
        <div className="ifd-body">
          {multipleSources && (
            <div role="tablist" aria-label="Flow source">
              {bindings.map((binding) => (
                <button key={binding.grammar} type="button" role="tab"
                  className={`ifd-btn ${binding.grammar === active.grammar ? 'ifd-btn--primary' : 'ifd-btn--ghost'}`}
                  disabled={isSaving}
                  aria-selected={binding.grammar === active.grammar}
                  onClick={() => { setActiveGrammar(binding.grammar); setError(null); setConflict(null); }}>
                  {binding.label}{dirty[binding.grammar] ? ' · unsaved' : ''}
                </button>
              ))}
            </div>
          )}
          <p className="ifd-blurb">
            {active.grammar === 'steps'
              ? 'Edit the process sequence. Use one numbered step per line; nest steps with :: in [parent].'
              : 'Edit actors, actions, commands and events in the domain interaction grammar.'}
          </p>
          <label className="ifd-label" htmlFor="fse-text">{textareaLabel}</label>
          <textarea id="fse-text" ref={textareaRef} className="ifd-textarea ifd-textarea--mono"
            value={draft}
            onChange={(event) => {
              setDrafts((previous) => ({ ...previous, [active.grammar]: event.target.value }));
              setDirty((previous) => ({ ...previous, [active.grammar]: true }));
              setError(null);
            }}
            rows={18} disabled={isSaving} spellCheck={false}
            placeholder={active.grammar === 'steps'
              ? '[1] Customer places an order\n[2] Team reviews it :: in [1]'
              : '[1] @!Customer > (action) PlaceOrder > &Order [via %sys Web]'}
          />
          <div className="fse-parse-summary">
            <span><strong>{parseResult.statements.length}</strong> statement{parseResult.statements.length === 1 ? '' : 's'}</span>
            {errCount > 0 && <span className="fse-parse-summary--err"> · <strong>{errCount}</strong> error{errCount === 1 ? '' : 's'}</span>}
            {warnCount > 0 && <span className="fse-parse-summary--warn"> · <strong>{warnCount}</strong> warning{warnCount === 1 ? '' : 's'}</span>}
            {dirty[active.grammar] && <span className="fse-parse-summary--dirty"> · unsaved changes</span>}
          </div>
          {(errCount > 0 || warnCount > 0) && (
            <details className="ifd-warn" open={errCount > 0}>
              <summary>Parser feedback ({errCount + warnCount})</summary>
              <ul className="fse-parse-issues">
                {parseResult.errors.slice(0, 30).map((issue, index) => (
                  <li key={index} className={issue.severity === 'error' ? 'fse-issue--err' : 'fse-issue--warn'}>
                    {issue.statementId != null && <code>[{issue.statementId}]</code>}{' '}
                    <strong>{issue.code}</strong>: {issue.message}
                  </li>
                ))}
                {parseResult.errors.length > 30 && <li>…+{parseResult.errors.length - 30} more</li>}
              </ul>
            </details>
          )}
          {error && <div className="ifd-error" role="alert">{error}</div>}
          {conflict && (
            <div className="ifd-conflict">
              <p>This session already has <strong>{conflict.existingStatementCount}</strong> statements. Saving will replace them.</p>
              <div className="ifd-conflict-actions">
                <button type="button" className="ifd-btn ifd-btn--ghost" onClick={() => setConflict(null)} disabled={isSaving}>Cancel</button>
                <button type="button" className="ifd-btn ifd-btn--danger" onClick={() => handleSave(true)} disabled={isSaving}>
                  {isSaving ? 'Saving…' : 'Overwrite and save'}
                </button>
              </div>
            </div>
          )}
        </div>
        <footer className="ifd-foot">
          <button type="button" className="ifd-btn ifd-btn--ghost" onClick={onClose} disabled={isSaving}>Cancel</button>
          <button type="button" className="ifd-btn ifd-btn--primary" onClick={() => handleSave(false)}
            disabled={isSaving || !sessionId || !dirty[active.grammar]}
            title={!sessionId ? 'Start a session before editing' : (!dirty[active.grammar] ? 'No changes to save' : '')}>
            {isSaving ? 'Saving…' : 'Save flow'}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default FlowSourceEditModal;
